import React from 'react';
import styled from 'styled-components';
import { ArrowRight } from 'lucide-react';
import colors from 'theme/colors';

/**
 * Peças visuais compartilhadas pelos três passos do cadastro de parceiro.
 *
 * Ficam aqui porque os passos antes repetiam o mesmo `FormContainer` e o mesmo
 * bloco de erro colado três vezes, com pequenas diferenças de espaçamento que
 * faziam a tela "pular" ao trocar de passo.
 */

export const StepShell = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

/* Tinta e acento do painel, as mesmas da /entrar. */
export const ink = '#1B1840';
export const muted = '#6E6C82';
export const accent = '#6111F0';

export const StepTitle = styled.h2`
    margin: 0;
    font-size: 25px;
    font-weight: 700;
    line-height: 32px;
    color: ${ink};

    &:focus {
        outline: none;
    }
`;

export const StepSubtitle = styled.p`
    margin: 4px 0 0;
    font-size: 14.6px;
    line-height: 20px;
    color: ${muted};
`;

export const FieldStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: 24px;
`;

export const FieldRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`;

export const Actions = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 26px;
`;

/** Botão principal dos passos: pílula roxa com a seta encostada à direita. */
export function SubmitButton({ children, ...props }) {
    return (
        <PrimaryButton type='submit' {...props}>
            <span>{children}</span>
            <ArrowRight size={18} strokeWidth={2} aria-hidden='true' />
        </PrimaryButton>
    );
}

const PrimaryButton = styled.button`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 50px;
    border: none;
    border-radius: 25px;
    background: #610DE7;
    color: ${colors.neutralLight};
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 8px 20px rgba(97, 13, 231, .24);
    transition: transform .15s ease, box-shadow .2s ease, background .2s ease;

    & > svg {
        position: absolute;
        right: 22px;
    }

    &:hover {
        background: #5609D3;
        box-shadow: 0 10px 24px rgba(97, 13, 231, .32);
    }

    &:active {
        transform: scale(.99);
    }

    &:focus-visible {
        outline: 2px solid ${accent};
        outline-offset: 3px;
    }

    &:disabled {
        opacity: .6;
        cursor: not-allowed;
    }
`;

/** Aviso de erro do passo inteiro (falha de rede, recusa do servidor). */
export const StepError = styled.p`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 18px 0 0;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid ${colors.danger}55;
    background: ${colors.danger}12;
    color: ${colors.dangerDark};
    font-size: 13px;
    line-height: 1.4;

    svg {
        flex-shrink: 0;
        margin-top: 1px;
    }
`;

/** Caixa neutra de contexto: o que vem a seguir, por que pedimos um dado. */
export const InfoBox = styled.div`
    margin-top: 20px;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1px solid ${colors.secondaryPurple}2E;
    background: ${colors.secondaryPurple}0D;
`;

export const InfoTitle = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    color: #6D14CC;
`;

export const InfoText = styled.p`
    margin: 0;
    font-size: 12px;
    line-height: 1.5;
    color: ${colors.night};
`;

export const InfoList = styled.ul`
    margin: 0;
    padding-left: 18px;
    font-size: 12px;
    line-height: 1.6;
    color: ${colors.night};
`;
